import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Download, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import api, { apiError } from "@/lib/api";

const PERIODS = [
  { v: "this_month", label: "This month" }, { v: "this_week", label: "This week" }, { v: "today", label: "Today" },
  { v: "yesterday", label: "Yesterday" }, { v: "all", label: "All time" },
];
const CATS = ["stock", "vehicle", "fuel", "tools", "software", "marketing", "wages", "office", "insurance", "general"];
const METHODS = ["cash", "card", "bank_transfer", "other"];
const COLORS = ["#FFB703", "#10B981", "#3B82F6", "#F97316", "#A855F7", "#EF4444", "#14B8A6", "#F59E0B", "#6366F1", "#84CC16"];

export default function AccountingTab() {
  const [period, setPeriod] = useState("this_month");
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const [s, e] = await Promise.all([
      api.get("/admin/accounting/summary", { params: { period } }),
      api.get("/admin/expenses", { params: { period } }),
    ]);
    setSummary(s.data); setExpenses(e.data);
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { date: open.exp.date, category: open.exp.category, description: open.exp.description || "", amount: parseFloat(open.exp.amount || 0), payment_method: open.exp.payment_method, vendor: open.exp.vendor || "" };
      if (open.mode === "new") await api.post("/admin/expenses", payload);
      else await api.put(`/admin/expenses/${open.exp.expense_id}`, payload);
      toast.success("Saved"); setOpen(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/expenses/${id}`); toast.success("Deleted"); load(); };

  const exportCSV = () => {
    const rows = [
      ["Type", "Date", "Category / Method", "Description / Customer", "Amount"],
      ...expenses.map(x => ["Expense", x.date, x.category, x.description || x.vendor, `-${x.amount}`]),
    ];
    Object.entries(summary?.income_by_payment_method || {}).forEach(([m, v]) => rows.push(["Income", "-", m, "Invoice payment", v]));
    const csv = rows.map(r => r.map(f => `"${String(f).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `eagle-eye-report-${period}.csv`; a.click();
  };

  const incByMethod = Object.entries(summary?.income_by_payment_method || {}).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));
  const expByCat = Object.entries(summary?.expenses_by_category || {}).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Accounting</h1>
          <p className="text-sm text-muted-foreground mt-1">Income from paid invoices minus expenses. Export a report for your accountant.</p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="accounting-period"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCSV} data-testid="accounting-export"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
          <Button onClick={() => setOpen({ mode: "new", exp: { date: new Date().toISOString().slice(0, 10), category: "stock", description: "", amount: 0, payment_method: "bank_transfer", vendor: "" } })} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="accounting-add-expense"><Plus className="h-4 w-4 mr-2" />Expense</Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-5"><TrendingUp className="h-5 w-5 text-emerald-600 mb-2" /><div className="text-xs uppercase tracking-widest text-muted-foreground">Income</div><div className="font-display text-3xl font-bold">${(summary.income_total || 0).toLocaleString()}</div></Card>
          <Card className="p-5"><TrendingDown className="h-5 w-5 text-red-500 mb-2" /><div className="text-xs uppercase tracking-widest text-muted-foreground">Expenses</div><div className="font-display text-3xl font-bold">${(summary.expenses_total || 0).toLocaleString()}</div></Card>
          <Card className="p-5 border-eagle-gold/40 bg-eagle-gold/5"><Wallet className="h-5 w-5 text-eagle-gold mb-2" /><div className="text-xs uppercase tracking-widest text-muted-foreground">Net</div><div className={`font-display text-3xl font-bold ${summary.net >= 0 ? "" : "text-red-500"}`}>${(summary.net || 0).toLocaleString()}</div></Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="font-display font-semibold mb-3">Income by payment method</div>
          {incByMethod.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">No paid invoices yet.</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={incByMethod} dataKey="value" nameKey="name" outerRadius={80} label={(e) => `${e.name}: $${e.value.toLocaleString()}`}>
                  {incByMethod.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="p-5">
          <div className="font-display font-semibold mb-3">Expenses by category</div>
          {expByCat.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">No expenses yet.</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={expByCat}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="name" fontSize={11} stroke="hsl(var(--muted-foreground))" /><YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" /><Tooltip formatter={(v) => `$${v.toLocaleString()}`} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} /><Bar dataKey="value" fill="#FFB703" radius={[6, 6, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border font-display font-semibold">Expenses ({expenses.length})</div>
        {expenses.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">No expenses yet — add your first one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="p-3">Date</th><th className="p-3">Category</th><th className="p-3">Vendor</th><th className="p-3">Description</th><th className="p-3">Method</th><th className="p-3 text-right">Amount</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {expenses.map(x => (
                <tr key={x.expense_id} className="border-t border-border">
                  <td className="p-3">{x.date}</td>
                  <td className="p-3 capitalize">{x.category}</td>
                  <td className="p-3">{x.vendor || "—"}</td>
                  <td className="p-3 max-w-md truncate">{x.description}</td>
                  <td className="p-3 capitalize">{(x.payment_method || "").replace("_", " ")}</td>
                  <td className="p-3 text-right font-semibold text-red-500">-${x.amount?.toLocaleString()}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setOpen({ mode: "edit", exp: x })}><Edit className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(x.expense_id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent>
          {open && (
            <>
              <DialogHeader><DialogTitle>{open.mode === "new" ? "New expense" : "Edit expense"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={open.exp.date} onChange={(e) => setOpen({ ...open, exp: { ...open.exp, date: e.target.value } })} data-testid="expense-date" /></div>
                <div><Label>Amount (AUD)</Label><Input type="number" step="0.01" value={open.exp.amount} onChange={(e) => setOpen({ ...open, exp: { ...open.exp, amount: e.target.value } })} data-testid="expense-amount" /></div>
                <div>
                  <Label>Category</Label>
                  <Select value={open.exp.category} onValueChange={(v) => setOpen({ ...open, exp: { ...open.exp, category: v } })}>
                    <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment method</Label>
                  <Select value={open.exp.payment_method} onValueChange={(v) => setOpen({ ...open, exp: { ...open.exp, payment_method: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map(m => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Vendor / paid to</Label><Input value={open.exp.vendor || ""} onChange={(e) => setOpen({ ...open, exp: { ...open.exp, vendor: e.target.value } })} /></div>
                <div className="col-span-2"><Label>Description</Label><Input value={open.exp.description || ""} onChange={(e) => setOpen({ ...open, exp: { ...open.exp, description: e.target.value } })} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(null)}>Cancel</Button><Button onClick={save} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="expense-save">Save</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
